// PosterGalleryPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PosterGalleryPage = () => {
    const [posters, setPosters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPoster, setSelectedPoster] = useState(null);

    useEffect(() => {
        const fetchPosters = async () => {
            try {
                setLoading(true);
                // Ensure this API endpoint is correctly set up on your server to fetch from Cloudinary
                const response = await fetch('/api/posters');

                if (!response.ok) {
                    const errorDetail = await response.text();
                    console.error('Error response from /api/posters:', response.status, response.statusText, errorDetail);
                    throw new Error(`HTTP error! Status: ${response.status}. Details: ${errorDetail.substring(0, 100)}...`);
                }

                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes('application/json')) {
                    const rawResponse = await response.text();
                    console.error(`Expected JSON from /api/posters, but received content type: ${contentType || 'none'}. Raw response: ${rawResponse.substring(0, 100)}...`);
                    throw new Error(`Invalid response format from server. Expected JSON, got ${contentType}.`);
                }

                const data = await response.json();

                if (data && data.error) {
                    throw new Error(`Server error: ${data.error}. Details: ${data.details || 'No additional details.'}`);
                }

                setPosters(data);
                setLoading(false);
            } catch (err) {
                console.error("Failed to load posters from API:", err);
                setError(
                    `Failed to load posters. Please ensure:
                    1. Your /api/posters route is set up correctly on the server.
                    2. The /api/posters route can successfully fetch data from Cloudinary.
                    Error: ${err.message}`
                );
                setLoading(false);
            }
        };

        fetchPosters();
    }, []);

    const openPosterModal = (poster) => {
        setSelectedPoster(poster);
    };

    const closePosterModal = () => {
        setSelectedPoster(null);
    };

    // --- Loading State ---
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 text-[#0A1128] flex flex-col items-center justify-center p-8 font-inter">
                <div className="w-full flex justify-start mb-8 max-w-5xl">
                    <Link to="/graphics-design" className="px-6 py-2 bg-[#C9B072] text-[#0A1128] font-semibold text-base rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90">
                        &larr; Back to Graphics & Design
                    </Link>
                </div>
                <p className="text-2xl text-gray-600 animate-pulse">Loading stunning posters from Cloudinary...</p>
            </div>
        );
    }

    // --- Error State ---
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 text-[#0A1128] flex flex-col items-center justify-center p-8 text-center font-inter">
                <div className="w-full flex justify-start mb-8 max-w-5xl">
                    <Link to="/graphics-design" className="px-6 py-2 bg-[#C9B072] text-[#0A1128] font-semibold text-base rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90">
                        &larr; Back to Graphics & Design
                    </Link>
                </div>
                <p className="text-2xl text-red-600 mb-4 whitespace-pre-line leading-relaxed">{error}</p>
                <p className="text-lg text-gray-700">Please check your server configuration and network connection.</p>
            </div>
        );
    }

    // --- Main Content Display ---
    return (
        <div className="min-h-screen bg-gray-50 text-[#0A1128] font-sans overflow-x-hidden">

            {/* Section 1: Hero/Introduction - Background with image */}
            <section
                className="py-20 text-center px-6 relative overflow-hidden bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url('https://i.imgur.com/pn2Gjr9.png')`,
                }}
            >
                {/* Overlay for dark background and improved text readability */}
                <div className="absolute inset-0 bg-black bg-opacity-20 z-0"></div>
                <div className="container mx-auto relative z-10 flex flex-col items-center justify-center">
                    {/* Added a div for the rounded black overlay */}
                    <div className="bg-black bg-opacity-60 p-8 rounded-lg max-w-4xl w-full">
                        {/* Back button at the top */}
                        <div className="w-full flex justify-start mb-12">
                            <Link to="/graphics-design" className="px-7 py-3 bg-[#C9B072] text-[#0A1128] font-bold text-lg rounded-full shadow-xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 hover:shadow-2xl">
                                &larr; Back to Graphics & Design
                            </Link>
                        </div>

                        {/* Page Title */}
                        <h2 className="text-5xl md:text-6xl font-extrabold text-[#C9B072] mb-12 drop-shadow-lg leading-tight">
                            Our Print & Marketing Portfolio
                        </h2>
                    </div>
                </div>
            </section>

            {/* Poster Grid */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto max-w-6xl">
                    {/* Keep grid columns for larger size */}
                    <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 gap-10 w-full mb-16">
                        {posters.length > 0 ? (
                            posters.map((poster) => (
                                <div
                                    key={poster.id || poster.imageUrl}
                                    className="relative w-full cursor-pointer overflow-hidden
                                        rounded-2xl shadow-lg transition-all duration-300 ease-in-out
                                        hover:scale-[1.02] hover:shadow-xl group flex justify-center items-center p-4 bg-white"
                                    onClick={() => openPosterModal(poster)}
                                >
                                    <img
                                        src={poster.imageUrl}
                                        className="w-full h-auto object-contain rounded-xl"
                                        alt={'Portfolio Image'}
                                        loading="lazy" // Added for performance
                                        onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/600x450/CCC/555?text=Image+Not+Found"; }}
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300 rounded-2xl"></div>
                                </div>
                            ))
                        ) : (
                            <p className="text-2xl text-gray-700 col-span-full text-center py-10">
                                No posters found. Please ensure your Cloudinary setup is correct and contains images.
                            </p>
                        )}
                    </div>

                    {/* Back button at the bottom */}
                    <Link to="/graphics-design" className="px-8 py-4 bg-[#C9B072] text-[#0A1128] font-bold text-xl rounded-full shadow-xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 hover:shadow-2xl">
                        Back to Graphics & Design
                    </Link>
                </div>
            </section>


            {/* Poster Modal */}
            {selectedPoster && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-90 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-fade-in"
                    onClick={closePosterModal}
                >
                    <div
                        className="relative max-w-5xl w-full max-h-[95vh] bg-gray-800 rounded-xl shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <button
                            onClick={closePosterModal}
                            className="absolute top-4 right-4 text-white text-4xl font-bold bg-gray-700 bg-opacity-75 rounded-full w-12 h-12 flex items-center justify-center
                                        transition hover:bg-red-600 hover:scale-110 z-10 focus:outline-none focus:ring-2 focus:ring-red-500"
                            aria-label="Close"
                        >
                            &times;
                        </button>
                        {/* Modal Image */}
                        <img
                            src={selectedPoster.imageUrl}
                            alt={'Selected Poster'}
                            className="max-w-full max-h-[85vh] object-contain mx-auto rounded-lg"
                            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/800x600/CCC/555?text=Image+Not+Found"; }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PosterGalleryPage;
